import { faArrowRight, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/sharp-regular-svg-icons';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import { useHttpClient } from '../../shared/hooks/http-hook';
import DrinksList from '../components/DrinksList';
import Influencers from '../components/Influencers';
import Journal from '../components/Journal';
import RadioButton from '../components/RadioButton.tsx';
import TrackerHeading from '../components/TrackerHeading';

function AlcoholTracker(props) {
  const { t } = useTranslation();
  const { sendRequest } = useHttpClient();
  const [drinkFree, setDrinkFree] = useState(false);
  const [hadDrink, setHadDrink] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [date, setDate] = useState(false);
  const [data, setData] = useState(null);
  const [currentTracker, setCurrentTracker] = useState(null);
  const [journal, setJournal] = useState(null);
  const [daysNotConsumedAlcohol, setDaysNotConsumedAlcohol] = useState(false);

  const getData = useCallback(
    async (alcoholDate) => {
      if (alcoholDate.length > 0) {
        try {
          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_URL}/trackers/alcohol/${alcoholDate}`
          );
          if (response && response.tracker?._id) {
            const { tracker } = response;
            setCurrentTracker({
              id: tracker._id,
              trackerDate: tracker.tracker_date.toString().slice(0, 10),
            });
            const parsedData = JSON.parse(tracker.data);
            setData(parsedData);
            setDrinkFree(parsedData.drink_free);
            setHadDrink(!parsedData.drink_free);
            setDaysNotConsumedAlcohol(response.daysNotConsumedAlcohol);
            setJournal({
              enhancers: tracker.enhancers,
              inhibitors: tracker.inhibitors,
              journal: tracker.journal,
            });
            setIsSaved(true);
          } else {
            setCurrentTracker({
              id: '',
              trackerDate: alcoholDate,
            });
            setData({
              drink_free: '',
              drink_alcohol: [],
            });
            setDrinkFree(false);
            setHadDrink(false);
            setDaysNotConsumedAlcohol(1);
            setJournal(() => ({
              enhancers: [],
              inhibitors: [],
              journal: '',
            }));
          }
        } catch (err) {}
      }
    },
    [sendRequest]
  );

  useEffect(() => {
    getData(date);
  }, [date, getData]);

  const clearTracker = () => {};

  const saveResponse = (location, stat) => {
    if (location === 'journal') {
      setJournal((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    } else {
      setData((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    }
  };

  const sendData = async () => {
    let method = 'POST';
    let url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;
    if (currentTracker.id !== '') {
      method = 'PUT';
      url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${currentTracker.id}`;
    }

    try {
      const response = await sendRequest(
        url,
        method,
        JSON.stringify({
          trackerDate: date,
          type: 'alcohol',
          data,
          hidden: false,
          enhancers: journal.enhancers,
          inhibitors: journal.inhibitors,
          journal: journal.journal,
          platform: 'web',
        })
      );
      if (response) {
        setIsSaved(true);

        props.closeTrackers();
      }
    } catch (err) {}
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendData();
  };

  const handleYesNo = (value) => {
    if (value === 'yes') {
      setDrinkFree(true);
      setHadDrink(false);
      saveResponse('data', {
        id: 'drink_free',
        value: true,
      });
      saveResponse('journal', {
        id: 'inhibitors',
        value: [],
      });
    } else if (value === 'no') {
      setDrinkFree(false);
      setHadDrink(true);
      saveResponse('data', {
        id: 'drink_free',
        value: false,
      });
      saveResponse('journal', {
        id: 'enhancers',
        value: [],
      });
    }
  };

  return (
    <div
      id="snm-alcohol"
      className="tracker-container"
      role="region"
      aria-label={`${t('T136')} ${t('tracker.region.title')}`}
    >
      <form className="tracker-form" onSubmit={handleSubmit} noValidate={false}>
        <div className="trackers-inner">
          <div className="back-to-main">
            <button
              className="back-btn"
              aria-label={t('A136')}
              title={t('A136')}
              onClick={props.handleBackToMain}
              type="button"
            >
              <FontAwesomeIcon icon={faCircleChevronLeft} />
            </button>
          </div>
          <TrackerHeading
            title="T137"
            setDate={setDate}
            isSaved={isSaved}
            clearTracker={clearTracker}
          />
          <div className="trackers-body" role="group" aria-label={t('modal.tracker.details')}>
            <fieldset className="ind-tracker no-padding">
              <legend className="title" id="alcohol-status-title">
                {t('T139')}
              </legend>
              <div className="buffer" />
              <div className="yes-no-btn fs-exclude">
                <RadioButton
                  ariaLabel={`${t('T139')} ${t('T066')}`}
                  label={t('T066')}
                  name="alcohol-status"
                  value="yes"
                  checked={drinkFree}
                  isSelected={drinkFree}
                  onChange={() => handleYesNo('yes')}
                  isRequired
                />
                <RadioButton
                  ariaLabel={`${t('T139')} ${t('T067')}`}
                  label={t('T067')}
                  name="alcohol-status"
                  value="no"
                  checked={hadDrink}
                  isSelected={hadDrink}
                  onChange={() => handleYesNo('no')}
                />
              </div>
              <input
                type="hidden"
                name="drink_free"
                value={data?.drink_free || ''}
                required
                aria-label={t('T139')}
              />
              <div className="buffer" />
            </fieldset>
            {drinkFree && (
              <div
                className="whc-cont alc fs-exclude"
                role="group"
                aria-label={`${t('trackers.alcohol.daysNotConsumedAlcohol')}`}
              >
                <div className="whc-body">
                  <div className="whc-heading">
                    <FontAwesomeIcon icon={faThumbsUp} /> {t('T108')}! {t('T561W')}
                    {daysNotConsumedAlcohol}
                    {t('T562W')}
                  </div>
                  <div className="whc-text">
                    {t('T563W')} {daysNotConsumedAlcohol} {t('T564W')}
                  </div>
                </div>
                <div className="icon-cont" aria-hidden="true">
                  <FontAwesomeIcon icon={faCalendar} />
                  <div className="days">{daysNotConsumedAlcohol}</div>
                </div>
              </div>
            )}
            {hadDrink && (
              <>
                <div className="ind-tracker">
                  <h2 className="title" id="drink-alcohol-title">
                    {t('T152')}
                  </h2>
                  <DrinksList questionid="drink_alcohol" data={data} saveResponse={saveResponse} />
                  <input
                    type="hidden"
                    name="drink_alcohol"
                    value={
                      data?.drink_alcohol?.some(
                        (drink) => drink.number > 0 && drink.type && drink.size && drink.strength
                      )
                        ? 'valid'
                        : ''
                    }
                    required={hadDrink}
                    aria-label={t('T152')}
                  />
                </div>
                <div className="ind-tracker">
                  <div className="tracker-content-heading">
                    <h3 className="title">{t('T140')}</h3>
                    <p className="optional-text">{t('T019')}</p>
                  </div>
                  <Influencers
                    alcohol
                    questionid={['enhancers', 'inhibitors']}
                    data={journal}
                    saveResponse={saveResponse}
                    redListAriaLabel={t('trackers.influncers.alcoholInhibitors')}
                  />
                  <div className="buffer" />
                </div>
              </>
            )}
            <div className="ind-tracker" role="group" aria-label={`${t('T136')} - ${t('T018')}`}>
              <Journal
                alcohol
                placeholder={hadDrink ? t('T545') : t('T546')}
                questionid="journal"
                data={journal}
                saveResponse={saveResponse}
              />
            </div>
          </div>
        </div>
        <div className="buttons">
          <Button onClick={props.closeTrackers} variant="secondary" color="dark" type="button">
            {t('T015')}
          </Button>
          <Button type="submit" icon={{ faIconDefinition: faArrowRight }} iconPosition="right">
            {t('T016')}
          </Button>
        </div>
      </form>
    </div>
  );
}
export default AlcoholTracker;
